// @ts-strict-ignore
import { useTranslation } from "react-i18next";

import { Typography } from "@aviary";
import { BackButton } from "@aviary/layouts";
import { useSearchParams, useLocation } from "@shared/react-router-dom/react-router-dom";
import type { PatientAttributes } from "@unauthenticated/shared/data/types/PatientAttributes.d";
import { PatientStoreSignUpForm } from "@unauthenticated/signupSignin/components/PatientStoreSignUpForm/PatientStoreSignUpForm";
import { SignUpHeader } from "@unauthenticated/signupSignin/components/SignUpHeader/SignUpHeader";
import { SignUpTitle } from "@unauthenticated/signupSignin/components/SignUpTitle/SignUpTitle";
import { StoreLandingLayout } from "@unauthenticated/signupSignin/components/StoreLandingLayout/StoreLandingLayout";
import { StoreLogo } from "@unauthenticated/signupSignin/components/StoreLogo/StoreLogo";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./PatientStoreSignUpPage.styles";

interface HistoryState {
  source?: "RX_SIGN_IN";
  email?: string;
}

interface Props {
  patientAttributes: PatientAttributes;
  additionalAttributes?: any;
  pageVersion?: string;
  storeId?: string;
  isPreview?: boolean;
  isStoreOwner: boolean;
  ssoErrors?: string;
  activePatientPromotion?: boolean;
  isStorefronts?: boolean;
}

const PatientStoreSignUpPage = ({
  patientAttributes,
  additionalAttributes,
  pageVersion,
  isPreview = false,
  isStoreOwner,
  storeId,
  ssoErrors,
  activePatientPromotion,
  isStorefronts,
}: Props) => {
  const { t } = useTranslation();
  const { storeLogo, organizationLogo, storeName, pracAvatar, isStoreIntake, iconLogo } =
    patientAttributes;
  const { state }: { state: HistoryState } = useLocation();
  const [searchParameters] = useSearchParams();

  const isIntake = searchParameters.get("intake") === "true";
  const requestedPractitionerId = searchParameters.get("requestedPractitionerId");

  const renderIntakeSubtitle = () => {
    if (!isStoreIntake || !isIntake || isStorefronts) {
      return null;
    }

    return <Typography css={styles.centered}>{t(l.intake.signUpDescription)}</Typography>;
  };

  const renderCreateAccountSubtitle = () => {
    if ((isStoreIntake && isIntake) || isStorefronts) {
      return null;
    }

    if (isSwiftFillSignIn) {
      return (
        <>
          <Typography type="h4">{t(l.auth.createYourAccount)}</Typography>
          <Typography css={styles.centered}>{t(l.auth.swiftfillSignupInstructions)}</Typography>
        </>
      );
    }
  };

  const isSwiftFillSignIn = state?.source === "RX_SIGN_IN";

  const signUpTitle = isStorefronts ? t(l.auth.createYourAccount) : storeName;

  return (
    <StoreLandingLayout
      storeName={storeName}
      storeId={storeId}
      pracAvatar={pracAvatar}
      isPreview={isPreview}
      isStoreOwner={isStoreOwner}
      isActivePromotion={activePatientPromotion}
      isStorefronts={isStorefronts}
      organizationLogo={organizationLogo}
      iconLogo={iconLogo}
    >
      <SignUpHeader isStorefronts={isStorefronts}>
        {!!isStorefronts && (
          <div css={styles.backButtonWrapper}>
            <BackButton text={t(l.common.Back)} isUnderLined={false} />
          </div>
        )}
        {!isStorefronts && <StoreLogo storeLogo={storeLogo} storeName={storeName} />}
        <SignUpTitle title={signUpTitle} />
        {renderIntakeSubtitle()}
        {renderCreateAccountSubtitle()}
      </SignUpHeader>
      <PatientStoreSignUpForm
        isStorefronts={isStorefronts}
        storeId={storeId}
        pageVersion={pageVersion}
        patientAttributes={patientAttributes}
        additionalAttributes={additionalAttributes}
        isPreview={isPreview}
        ssoErrors={ssoErrors}
        preFilledEmail={state?.email}
        isSwiftFillSignIn={isSwiftFillSignIn}
        isIntake={isIntake}
        requestedPractitionerId={requestedPractitionerId}
      />
    </StoreLandingLayout>
  );
};

export { PatientStoreSignUpPage };
